import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * ProcessTimelinePro – Polished, animated, and responsive
 * - Desktop: elegant S‑curve SVG with gradient stroke + glow, staggered cards
 * - Mobile: clean vertical timeline with numbered nodes and smooth reveal
 * - Tailwind‑only; no external libs; respects prefers‑reduced‑motion
 * - Easy to theme via `accent` prop (Tailwind color classes) and `title`, `subtitle`
 */

export default function ProcessTimelinePro({
  title = "Unser Ablauf – klar & professionell",
  subtitle = "Von der ersten Anfrage bis zur Abnahme: transparent, planbar und effizient.",
  accent = {
    ring: "ring-emerald-300/60",
    bgSoft: "bg-emerald-50",
    text: "text-emerald-700",
    pill: "border-emerald-200 bg-emerald-50 text-emerald-700",
    dot: "fill-emerald-500",
    stroke: "stroke-emerald-500",
    gradFrom: "#34d399",
    gradTo: "#10b981",
  },
  steps = DEFAULT_STEPS,
}) {
  return (
    <section className="relative isolate py-16 sm:py-20 bg-white">
      <div className="mx-auto max-w-6xl px-6 lg:px-12">
        <header className="mb-12 text-center">
          <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">{title}</h3>
          <p className="mt-3 text-sm sm:text-base text-gray-600">{subtitle}</p>
        </header>

        {/* Desktop / large: Curved SVG path + floating step cards */}
        <DesktopCurve steps={steps} accent={accent} className="hidden md:block" />

        {/* Mobile: vertical timeline */}
        <MobileTimeline steps={steps} accent={accent} className="md:hidden" />
      </div>
    </section>
  );
}

/* ===================== Desktop Curve ===================== */

function DesktopCurve({ steps, accent, className = "" }) {
  const { ref, inViewOnce } = useInViewOnce({ threshold: 0.2 });
  const reduced = usePrefersReducedMotion();

  // Anchor positions (as percentage of SVG width)
  const anchors = useMemo(
    () => [
      { xPct: 0.14, y: 182 },
      { xPct: 0.37, y: 120 },
      { xPct: 0.62, y: 205 },
      { xPct: 0.87, y: 118 },
    ],
    []
  );

  return (
    <div ref={ref} className={className}>
      <div className="relative">
        {/* SVG path background */}
        <svg viewBox="0 0 1000 260" className="w-full h-[280px] text-gray-200" aria-hidden="true">
          <defs>
            <linearGradient id="ptpGrad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={accent.gradFrom} />
              <stop offset="100%" stopColor={accent.gradTo} />
            </linearGradient>
            <filter id="ptpGlow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="6" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Base track */}
          <path
            d="M40,210 C240,120 360,120 520,180 C680,240 820,150 960,90"
            fill="none"
            stroke="currentColor"
            strokeWidth="6"
            strokeLinecap="round"
            className="opacity-40"
          />

          {/* Animated progress */}
          <AnimatedStroke
            d="M40,210 C240,120 360,120 520,180 C680,240 820,150 960,90"
            play={inViewOnce && !reduced}
            gradientId="ptpGrad"
          />

          {/* Soft glow dots at step positions */}
          {anchors.map((a, i) => (
            <g key={i} transform={`translate(${a.xPct * 1000},${a.y})`} filter="url(#ptpGlow)">
              <circle r="10" className={accent.dot} opacity="0.18" />
              <circle r="5.5" className={accent.dot} opacity="0.95" />
            </g>
          ))}
        </svg>

        {/* Floating cards aligned to path anchors */}
        <ul className="pointer-events-none">
          {steps.map((s, i) => (
            <li key={i} className="pointer-events-auto absolute" style={anchorStylePct(i)}>
              <StepCard index={i + 1} title={s.title} text={s.text} Icon={s.Icon} accent={accent} revealDelay={i * 90} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  function anchorStylePct(i) {
    const a = anchors[i];
    const up = i % 2 === 0;
    return {
      left: `calc(${a.xPct * 100}% - 150px)`, // ~300px card width
      top: up ? `${a.y - 160}px` : `${a.y + 20}px`,
    };
  }
}

function StepCard({ index, title, text, Icon, accent, revealDelay = 0 }) {
  const { ref, inViewOnce } = useInViewOnce({ threshold: 0.1 });
  return (
    <article
      ref={ref}
      className={[
        "w-[300px] rounded-2xl border bg-white/90 backdrop-blur",
        "border-gray-100 p-5 shadow-sm transition-all",
        "hover:shadow-lg hover:-translate-y-0.5",
        inViewOnce ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
      ].join(" ")}
      style={{
        transition: `opacity 600ms cubic-bezier(.2,.7,.2,1) ${revealDelay}ms, transform 600ms cubic-bezier(.2,.7,.2,1) ${revealDelay}ms, box-shadow 300ms`,
      }}
    >
      <div className="mb-3 flex items-center justify-between">
        <div className={["inline-flex h-11 w-11 items-center justify-center rounded-xl","border border-gray-200 bg-gray-50 text-gray-800 shadow-sm"].join(" ")}
        >
          <Icon className="h-6 w-6" />
        </div>
        <span className={["inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold",accent.pill].join(" ")}>Schritt {index}</span>
      </div>
      <h4 className="text-base font-semibold text-gray-900">{title}</h4>
      <p className="mt-1 text-sm text-gray-600">{text}</p>
    </article>
  );
}

function AnimatedStroke({ d, play, gradientId }) {
  const pathRef = useRef(null);
  useEffect(() => {
    const el = pathRef.current;
    if (!el) return;
    const len = el.getTotalLength();
    el.style.strokeDasharray = `${len}`;
    el.style.strokeDashoffset = play ? "0" : `${len}`;
    el.style.transition = play ? "stroke-dashoffset 1400ms cubic-bezier(.2,.7,.2,1)" : "none";
  }, [play]);
  return (
    <path
      ref={pathRef}
      d={d}
      fill="none"
      stroke={`url(#${gradientId})`}
      strokeWidth="6"
      strokeLinecap="round"
      style={{ strokeDasharray: 1000, strokeDashoffset: 1000, filter: "url(#ptpGlow)" }}
    />
  );
}

/* ===================== Mobile Timeline ===================== */

function MobileTimeline({ steps, accent, className = "" }) {
  const [progress, setProgress] = useState(0);
  const [revealedCount, setRevealedCount] = useState(0);

  function handleReveal(idx) {
    const next = Math.max(revealedCount, idx + 1);
    setRevealedCount(next);
    const p = next / steps.length;
    setProgress(p);
  }

  return (
    <div className={className}>
      <div className="relative pl-4">
        {/* base rail */}
        <span className="absolute left-[10px] top-0 h-full w-[2px] bg-gray-200" aria-hidden />
        {/* animated progress rail */}
        <span
          className="absolute left-[10px] top-0 w-[2px]"
          style={{
            height: `${Math.max(0.06, progress) * 100}%`,
            background: `linear-gradient(to bottom, ${accent.gradFrom}, ${accent.gradTo})`,
            transition: "height 700ms cubic-bezier(.2,.7,.2,1)",
            borderRadius: "9999px",
          }}
          aria-hidden
        />

        <ol>
          {steps.map((s, i) => (
            <MobileItem key={i} index={i} step={s} accent={accent} onReveal={handleReveal} isDone={i < revealedCount} />
          ))}
        </ol>
      </div>
    </div>
  );
}

function MobileItem({ index, step, accent, onReveal, isDone }) {
  const { ref, inViewOnce } = useInViewOnce({ threshold: 0.2 });

  useEffect(() => {
    if (inViewOnce) onReveal(index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inViewOnce]);

  return (
    <li ref={ref} className="relative ml-2 pb-9 last:pb-0">
      {/* Node + number */}
      <span
        className={[
          "absolute -left-[14px] top-0 inline-flex h-8 w-8 items-center justify-center",
          "rounded-full font-bold text-white text-xs ring-2 ring-white shadow",
          accent.ring,
          "bg-emerald-500",
          inViewOnce ? "scale-100" : "scale-90",
          "transition-transform duration-500",
        ].join(" ")}
      >
        {index + 1}
      </span>
      {/* one-time ping when revealed */}
      {inViewOnce && (
        <span className="pointer-events-none absolute -left-[17px] top-[-3px] h-10 w-10 rounded-full bg-emerald-400/40 animate-ping" />
      )}

      <div
        className={[
          "ml-6 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm",
          "active:scale-[0.98] transition-all",
          inViewOnce ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3",
        ].join(" ")}
        style={{ transition: `opacity 600ms ${index * 60}ms, transform 600ms ${index * 60}ms, box-shadow 300ms` }}
      >
        <div className="mb-2 flex items-center gap-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200 bg-gray-50 text-gray-800 shadow-sm">
            <step.Icon className={"h-5 w-5 " + (isDone ? "opacity-100" : "opacity-80")} />
          </div>
          <h4 className="text-sm font-semibold text-gray-900">{step.title}</h4>
        </div>
        <p className="text-sm leading-relaxed text-gray-600">{step.text}</p>
      </div>
    </li>
  );
}

/* ===================== Hooks ===================== */

function useInViewOnce({ threshold = 0.25 } = {}) {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current || seen) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen, threshold]);
  return { ref, inViewOnce: seen };
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(!!mq.matches);
    on();
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

/* ===================== Default Steps & Icons ===================== */

const DEFAULT_STEPS = [
  { title: "Anfrage", text: "Kontakt per Formular oder Telefon – wir melden uns umgehend.", Icon: PhoneIcon },
  { title: "Angebot", text: "Transparente Beratung & individuelles Angebot ohne versteckte Kosten.", Icon: ClipboardIcon },
  { title: "Ausführung", text: "Pünktliches, geschultes Team – gründlich und termintreu.", Icon: BroomIcon },
  { title: "Abnahme", text: "Gemeinsamer Check & Feedback – erst zufrieden, dann abgeschlossen.", Icon: CheckIcon },
];

function PhoneIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.9v3a2 2 0 01-2.18 2c-3.2-.4-6.2-1.6-8.63-3.07A19.5 19.5 0 013.16 9.81 19.8 19.8 0 01.98 2.72 2 2 0 013.06.99H6a2 2 0 012 1.72c.12.9.33 1.78.63 2.63a2 2 0 01-.45 2.11L7.1 7.1a16 16 0 008.48 8.48l.93-1.08a2 2 0 012.11-.45c.85.3 1.73.51 2.63.63A2 2 0 0122 16.92z" />
    </svg>
  );
}
function ClipboardIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="4" y="4" width="16" height="18" rx="2" />
      <path d="M9 2h6v4H9z" />
    </svg>
  );
}
function BroomIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 21l6-6" />
      <path d="M15 3l6 6" />
      <path d="M8 16l5-5" />
    </svg>
  );
}
function CheckIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
